<template>
  <v-dialog v-model="dialog" width="700px" scrollable persistent>
    <template v-slot:activator="{ on }">
      <v-text-field outlined hide-details :dense="dense" :value="textFieldValue" v-on="on" />
    </template>

    <v-card>
      <v-card-title>Selecione um período</v-card-title>
      <v-card-text>
        <v-container fluid class="pa-0">
          <v-row>
            <v-col>
              <v-select v-model="selectedPeriod" label="Opções" hide-details solo :items="selectOptions" @input="selectDates" />
            </v-col>
          </v-row>
          <mf-info-card variant="info" :v-html="infoCardHtml" class="my-0" />
          <v-row>
            <v-col>
              <mf-date-range-picker
                label="Data inicial - Data final"
                :disabled="isDynamicDate"
                :initial-date.sync="initialDate"
                :final-date.sync="finalDate"
                :min="firstDay"
                :max="lastDay"
                :error-messages="rangeError"
                @input="setSelectedPeriodAsCustom"
              />
            </v-col>
          </v-row>
        </v-container>
        <mf-action-buttons
          :primary-button="{
            text: 'Confirmar',
            action: submit,
            isVisible: true,
            isDisabled: Boolean(rangeError),
          }"
        />
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapGetters, mapState, mapActions } from 'vuex'
import { DateTime } from 'luxon'

export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    defaultDate: {
      type: String,
      default: 'm-0=',
      validator: (value) => ['d-0=', 'd-1=', 'd-7<=', 'd-30<=', 'd-90<=', 'm-0=', 'm-1=', 'non_empty'].indexOf(value) !== -1,
    },
    maxFilterDays: {
      type: Number,
      default: null,
    },
    useCurrentDateAsLastDay: {
      type: Boolean,
      default: false,
    },
    dense: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dialog: false,
    selectedPeriod: '',
    initialDate: '',
    finalDate: '',
    rangeError: '',
  }),

  computed: {
    ...mapGetters({
      primeiroRegistro: 'empresaPrimeiroRegistro',
    }),
    ...mapState({
      lastUpdate: (state) => state.lastUpdate,
    }),
    infoCardHtml() {
      if (this.isDynamicDate) {
        return '<span style="color: #004D99">A data selecionada é <b>dinâmica</b> e <b>será</b> atualizada diariamente.</span>'
      }
      return '<span style="color: #004D99">A data selecionada é <b>fixa</b> e <b>não será</b> atualizada diariamente.</span>'
    },
    isDynamicDate() {
      return this.selectedPeriod !== 'personalized_period'
    },
    textFieldValue() {
      switch (this.selectedPeriod) {
        case 'd-0=':
          return 'Dia mais recente'
        case 'd-1=':
          return 'Dia anterior'
        case 'd-7<=':
          return 'Últimos 7 dias'
        case 'd-30<=':
          return 'Últimos 30 dias'
        case 'd-90<=':
          return 'Últimos 90 dias'
        case 'm-0=':
          return 'Mês mais recente'
        case 'm-1=':
          return 'Mês anterior'
        case 'non_empty':
          return 'Desde o início'
        case 'personalized_period':
          const formattedInitialDate = DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          const formattedFinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd').toFormat('dd/MM/yyyy')
          return `${formattedInitialDate} - ${formattedFinalDate}`
        default:
          return ''
      }
    },

    selectOptions() {
      return [
        {
          text: 'Dia mais recente',
          value: 'd-0=',
        },
        {
          text: 'Dia anterior',
          value: 'd-1=',
        },
        {
          text: 'Últimos 7 dias',
          value: 'd-7<=',
        },
        {
          text: 'Últimos 30 dias',
          value: 'd-30<=',
        },
        {
          text: 'Últimos 90 dias',
          value: 'd-90<=',
        },
        {
          text: 'Mês mais recente',
          value: 'm-0=',
        },
        {
          text: 'Mês anterior',
          value: 'm-1=',
        },
        ...(!this.maxFilterDays ? [{ text: 'Desde o início', value: 'non_empty' }] : []),
        {
          text: 'Período personalizado',
          value: 'personalized_period',
        },
      ]
    },

    firstDay() {
      if (this.primeiroRegistro) return DateTime.fromFormat(this.primeiroRegistro.toString(), 'yyyy-MM-dd HH:mm:ss').toFormat('yyyy-MM-dd')
      return ''
    },
    lastDay() {
      if (this.useCurrentDateAsLastDay) return DateTime.now().toFormat('yyyy-MM-dd')
      if (!this.lastUpdate.last_updated) return ''
      return DateTime.fromFormat(this.lastUpdate.last_updated.toString(), 'yyyyMMdd').toFormat('yyyy-MM-dd')
    },
  },
  watch: {
    dialog(value) {
      if (value) {
        if (!this.value) {
          this.selectDates(this.defaultDate)
        }
      }
    },
    value(value) {
      if (!value) {
        this.selectedPeriod = this.defaultDate
      }
    },
    initialDate(value) {
      if (value) {
        const InitialDate = DateTime.fromFormat(value[0], 'yyyy-MM-dd')
        const FinalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd')
        if (InitialDate > FinalDate) {
          this.finalDate = InitialDate.toFormat('yyyy-MM-dd')
        }
      }
    },
    finalDate(value) {
      if (value) {
        const FinalDate = DateTime.fromFormat(value[1], 'yyyy-MM-dd')
        const InitialDate = this.initialDate ? DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd') : DateTime.now().startOf('year')
        if (FinalDate < InitialDate) {
          this.initialDate = FinalDate.toFormat('yyyy-MM-dd')
        }
      }
    },
  },
  mounted() {
    if (this.value.field === 'date_dynamic') {
      this.selectedPeriod = this.value.values[0]
      this.selectDates(this.value.values[0])
    } else if (this.value.field === 'date_fixed') {
      this.selectedPeriod = 'personalized_period'
      this.initialDate = this.value.values[0]
      this.finalDate = this.value.values[1]
      this.selectDates('personalized_period')
    }
  },
  methods: {
    ...mapActions(['setDateFilterSelectedPeriod']),
    checkError() {
      if (!this.maxFilterDays) {
        this.rangeError = ''
        return
      }
      const initialDate = DateTime.fromFormat(this.initialDate, 'yyyy-MM-dd')
      const finalDate = DateTime.fromFormat(this.finalDate, 'yyyy-MM-dd')
      if (finalDate.diff(initialDate, ['days']).days > this.maxFilterDays) {
        this.rangeError = `O período selecionado deve ter no máximo ${this.maxFilterDays} dias.`
      } else {
        this.rangeError = ''
      }
    },
    submit() {
      if (this.isDynamicDate) {
        this.$emit('input', {
          field: 'date_dynamic',
          values: [this.selectedPeriod],
        })
      } else {
        this.$emit('input', {
          field: 'date_fixed',
          values: [this.initialDate, this.finalDate],
        })
      }

      this.dialog = false
    },
    selectDates(value) {
      this.setDateFilterSelectedPeriod(value)

      // Handle special cases first
      if (value === 'non_empty') {
        this.setInitialAndFinalDate({ initial: this.firstDay, final: this.daysAgo(0) })
        return
      }

      if (value === 'personalized_period') {
        this.setInitialAndFinalDate({ initial: this.daysAgo(0), final: this.daysAgo(0) })
        return
      }

      // Parse dynamic format: d-0=, d-1=, d-7<=, m-0=, etc.
      const match = value.match(/^([dm])-(\d+)([=<=]+)$/)
      if (!match) {
        console.warn('Invalid date format:', value)
        return
      }

      const [, unit, number, operator] = match
      const num = parseInt(number, 10)

      if (unit === 'd') {
        // Day-based calculations
        if (operator === '=') {
          // Single day: d-0= (today), d-1= (yesterday)
          const targetDate = this.daysAgo(num)
          this.setInitialAndFinalDate({ initial: targetDate, final: targetDate })
        } else if (operator === '<=') {
          // Range: d-7<= (last 7 days), d-30<= (last 30 days)
          this.setInitialAndFinalDate({ initial: this.daysAgo(num), final: this.daysAgo(0) })
        }
      } else if (unit === 'm') {
        // Month-based calculations
        if (operator === '=') {
          this.setInitialAndFinalDate({
            initial: this.firstDayOfMonthAgo(num),
            final: this.lastDayOfMonthAgo(num),
          })
        }
      }
    },
    setInitialAndFinalDate({ initial, final }) {
      this.initialDate = initial
      this.finalDate = final
    },
    setDefaultSelectedPeriod() {
      this.selectedPeriod = this.defaultDate
      this.setDateFilterSelectedPeriod(this.defaultDate)
    },
    setSelectedPeriodAsCustom() {
      this.checkError()
      const customPeriodValue = 'personalized_period'
      this.selectedPeriod = customPeriodValue
      this.setDateFilterSelectedPeriod(customPeriodValue)
    },
    daysAgo(days) {
      if (!this.lastDay) return ''
      const val = DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ days: -days }).toFormat('yyyy-MM-dd')
      return val
    },
    firstDayOfMonthAgo(months) {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ months: -months }).startOf('month').toFormat('yyyy-MM-dd')
    },
    lastDayOfMonthAgo(months) {
      if (!this.lastDay) return ''
      return DateTime.fromFormat(this.lastDay, 'yyyy-MM-dd').plus({ months: -months }).endOf('month').toFormat('yyyy-MM-dd')
    },
  },
}
</script>
<style lang="scss">
.data-select {
  .v-text-field__details {
    margin: 0 !important;
  }
}

.date-card {
  user-select: none;
  cursor: pointer;
}
</style>
